<template>
  <div class="hs-date-range">
    <div v-if="label" class="hs-date-range__label">{{ label }}</div>
    <div class="hs-date-range__inputs">
      <hs-date-input
        v-model="startInput"
        label="From"
        placeholder="Start Date"
        :max="endInput"
      />
      <hs-date-input
        v-model="endInput"
        label="To"
        placeholder="End Date"
        :min="startInput"
      />
    </div>
    <div class="hs-date-range__actions">
      <hs-button
        v-for="preset in presets"
        :key="preset.label"
        size="sm"
        variant="secondary"
        @click="applyPreset(preset)"
      >
        {{ preset.label }}
      </hs-button>
    </div>
  </div>
</template>

<script>
import HsButton from "./HsButton.vue";
import HsDateInput from "./HsDateInput.vue";

const today = () => new Date();

export default {
  name: "HsDateRangePicker",
  components: { HsButton, HsDateInput },
  props: {
    modelValue: {
      type: Object,
      default: () => ({ start: new Date(), end: new Date() }),
    },
    label: { type: String, default: "" },
    isDark: { type: Boolean, default: false },
    columns: { type: [Number, Object], default: 2 },
  },
  emits: ["update:modelValue", "change"],
  data() {
    return {
      localValue: this.modelValue,
      presets: [
        {
          label: "Today",
          getValue: () => ({ start: today(), end: today() }),
        },
        {
          label: "Yesterday",
          getValue: () => {
            const date = today();
            date.setDate(date.getDate() - 1);
            return { start: date, end: date };
          },
        },
        {
          label: "Current week",
          getValue: () => {
            const end = today();
            const start = new Date(end);
            const day = start.getDay();
            const daysFromMonday = day === 0 ? 6 : day - 1;
            start.setDate(start.getDate() - daysFromMonday);
            return { start, end };
          },
        },
        {
          label: "Current month",
          getValue: () => {
            const end = today();
            return { start: new Date(end.getFullYear(), end.getMonth(), 1), end };
          },
        },
        {
          label: "Last month",
          getValue: () => {
            const end = today();
            return {
              start: new Date(end.getFullYear(), end.getMonth() - 1, 1),
              end: new Date(end.getFullYear(), end.getMonth(), 0),
            };
          },
        },
        {
          label: "Last year",
          getValue: () => {
            const end = today();
            return {
              start: new Date(end.getFullYear() - 1, 0, 1),
              end: new Date(end.getFullYear() - 1, 11, 31),
            };
          },
        },
      ],
    };
  },
  watch: {
    modelValue(value) {
      this.localValue = value;
    },
  },
  computed: {
    startInput: {
      get() {
        return this.toDateInput(this.localValue?.start);
      },
      set(value) {
        const nextValue = {
          ...this.localValue,
          start: this.toDate(value),
        };
        this.localValue = nextValue;
        this.emitValue(nextValue);
      },
    },
    endInput: {
      get() {
        return this.toDateInput(this.localValue?.end);
      },
      set(value) {
        const nextValue = {
          ...this.localValue,
          end: this.toDate(value),
        };
        this.localValue = nextValue;
        this.emitValue(nextValue);
      },
    },
  },
  methods: {
    toDate(value) {
      if (!value) return null;
      if (value instanceof Date) return value;
      const parsed = new Date(`${value}T12:00:00`);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    },
    toDateInput(value) {
      const date = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      return [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
      ].join("-");
    },
    emitValue(value) {
      this.$emit("update:modelValue", value);
      this.$emit("change", value);
    },
    applyPreset(preset) {
      this.localValue = preset.getValue();
      this.emitValue(this.localValue);
    },
  },
};
</script>

<style scoped>
.hs-date-range {
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 94%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 22px;
  padding: 1rem;
}

.hs-date-range__label {
  color: var(--hs-text, #0f172a);
  font-size: 0.86rem;
  font-weight: 950;
  margin-bottom: 0.7rem;
}

.hs-date-range__inputs {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.hs-date-range__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-top: 0.75rem;
}

@media (max-width: 520px) {
  .hs-date-range__inputs {
    grid-template-columns: 1fr;
  }
}
</style>
